/**
 * Lumio generation on the SEO page: which fields each "Generate with Lumio" control asks for, and which fields are
 * still showing what Lumio wrote (highlighted until the owner edits them). Pure.
 */
import type {
  SiteSeoDocument,
  SiteSeoGenerated,
  SiteSeoGenerateField,
  SiteSeoGenerateRequest,
  SiteSeoRoute,
} from './seo-types';

/** The credit price row of one `site-seo/generate` call (one charge per call, whatever the fields). */
export const SEO_PRICE_KEY = 'site_seo';

/** What a Generate control writes: a route's search result, the footer text as prose or as a FAQ, the AI texts. */
export type GenerateTarget = 'search' | 'footer-text' | 'footer-faq' | 'ai';

/** The site level (no path) in a highlight key. */
const SITE_KEY = '*';

/**
 * The request a target sends. Search is offered on exact and pattern routes only (the panel hides it on a footer
 * wildcard); the footer writes for the route, or the site when `route` is null; the AI texts are always the site's.
 */
export const generateRequestFor = (
  target: GenerateTarget,
  route: SiteSeoRoute | null,
  language: string,
): SiteSeoGenerateRequest => {
  const at = route ? { path: route.path } : {};
  switch (target) {
    case 'search':
      return { ...at, language, fields: ['title', 'description'] };
    case 'footer-text':
      return { ...at, language, fields: ['footer_text'], footer_mode: 'text' };
    case 'footer-faq':
      return { ...at, language, fields: ['footer_text'], footer_mode: 'faq' };
    case 'ai':
      return { language, fields: ['llms_summary', 'llms_facts'] };
  }
};

const keyOf = (path: string | undefined, language: string, field: SiteSeoGenerateField): string =>
  `${path ?? SITE_KEY}|${language}|${field}`;

/** `${path ?? '*'}|${language}|${field}` for each asked field the answer wrote. */
export const highlightKeys = (req: SiteSeoGenerateRequest, generated: SiteSeoGenerated): string[] =>
  req.fields.filter((field) => Boolean(generated[field])).map((field) => keyOf(req.path, req.language, field));

/** Highlight key → the text Lumio wrote there. */
export type GeneratedMarks = Readonly<Record<string, string>>;

/** `marks` with this answer's written fields marked (a later answer for the same field replaces the mark). */
export const markGenerated = (
  marks: GeneratedMarks,
  req: SiteSeoGenerateRequest,
  generated: SiteSeoGenerated,
): GeneratedMarks => {
  const next: Record<string, string> = { ...marks };
  for (const field of req.fields) {
    const value = generated[field];
    if (value) next[keyOf(req.path, req.language, field)] = value;
  }
  return next;
};

/** The draft's value of a generated field (`path` null = the site; the AI texts are not per language). */
const valueAt = (
  doc: SiteSeoDocument,
  path: string | null,
  language: string,
  field: SiteSeoGenerateField,
): string | undefined => {
  if (path === null) {
    if (field === 'llms_summary' || field === 'llms_facts') return doc.site.ai[field];
    if (field === 'description' || field === 'footer_text') return doc.site[field]?.[language];
    return undefined;
  }
  if (field === 'llms_summary' || field === 'llms_facts') return undefined;
  return doc.routes.find((r) => r.path === path)?.[field]?.[language];
};

const FIELDS: readonly SiteSeoGenerateField[] = ['title', 'description', 'footer_text', 'llms_summary', 'llms_facts'];

/**
 * The fields of `path` (null = the site) in `language` that still hold exactly what Lumio wrote: an edit of a field
 * changes its value, which ends that field's highlight and no other.
 */
export const generatedFieldsAt = (
  marks: GeneratedMarks,
  doc: SiteSeoDocument,
  path: string | null,
  language: string,
): SiteSeoGenerateField[] =>
  FIELDS.filter((field) => {
    const written = marks[keyOf(path ?? undefined, language, field)];
    return written !== undefined && valueAt(doc, path, language, field) === written;
  });
